import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog, type ConfirmVariant } from './ConfirmDialog';

/**
 * `window.confirm` and `window.alert`, as themed dialogs.
 *
 * The browser's own dialog was doing this job across the admin screens, and the
 * call sites are all shaped the same way — ask, then bail out on a no. Keeping
 * that shape is the point of the imperative API: a screen swapping to this one
 * changes the question, not its control flow.
 *
 *     const confirm = useConfirm();
 *     if (!(await confirm({ message: 'Delete this rule?' }))) return;
 *
 * Passing `onConfirm` is the better form when the action is a request. The
 * dialog then stays up with a spinner while it runs, closes when it resolves,
 * and — unlike `window.confirm`, which was gone before the request even left —
 * shows the failure in place if it rejects, so the user learns nothing happened.
 *
 *     await confirm({
 *       message: t('Users.ConfirmDelete', …),
 *       onConfirm: () => deleteUser.mutateAsync(user.id),
 *     });
 *
 * `useAlert` is the same dialog with one button, for the messages that were
 * `window.alert`. It resolves when the user acknowledges, which callers rarely
 * need — `void alert({ … })` is the usual form.
 *
 *     const alert = useAlert();
 *     alert({ variant: 'danger', message: t('Grid.ExportError', …) });
 *
 * One dialog exists at a time, mounted once by the provider near the root. A
 * second request while one is open answers the first with `false`.
 */

export interface ConfirmOptions {
  title?: ReactNode;
  message: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  /** Defaults to `danger`; most confirmations in this app guard a deletion. */
  variant?: ConfirmVariant;
  /** Name of a glyph in the shared sprite; overrides the variant's default. */
  icon?: string;
  /** Bootstrap's modal widths; unset is the standard 500px. */
  size?: 'sm' | 'lg' | 'xl';
  /**
   * The action itself, run while the dialog stays open. Resolving closes it and
   * answers `true`; rejecting keeps it open, shows the error, and lets the user
   * retry or cancel.
   */
  onConfirm?: () => void | Promise<unknown>;
}

export interface AlertOptions {
  title?: ReactNode;
  message: ReactNode;
  /** The single button's label; defaults to OK. */
  dismissLabel?: ReactNode;
  /** Defaults to `info`. `success` and `danger` are what outcomes usually want. */
  variant?: ConfirmVariant;
  /** Name of a glyph in the shared sprite; overrides the variant's default. */
  icon?: string;
  /** Bootstrap's modal widths; unset is the standard 500px. */
  size?: 'sm' | 'lg' | 'xl';
}

/** Resolves true if the user confirmed (and `onConfirm`, if given, succeeded). */
export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

/** Resolves once the user has acknowledged the message. */
export type AlertFn = (options: AlertOptions) => Promise<void>;

const ConfirmContext = createContext<{ confirm: ConfirmFn; alert: AlertFn } | null>(null);

/** Both kinds settle through `resolve`; an alert simply ignores the answer. */
type Request =
  | { kind: 'confirm'; options: ConfirmOptions; resolve: (confirmed: boolean) => void }
  | { kind: 'alert'; options: AlertOptions; resolve: (confirmed: boolean) => void };

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  const [request, setRequest] = useState<Request | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The open request is read from callbacks that outlive the render they were
  // created in (an awaited onConfirm, a click after a re-render), so the ref is
  // the copy those read.
  const current = useRef<Request | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    current.current?.resolve(confirmed);
    current.current = null;
    setRequest(null);
    setPending(false);
    setError(null);
  }, []);

  const open = useCallback((next: Request) => {
    // Whatever was open loses its answer rather than its promise.
    current.current?.resolve(false);

    current.current = next;
    setRequest(next);
    setPending(false);
    setError(null);
  }, []);

  const confirm = useCallback<ConfirmFn>(
    (options) => new Promise<boolean>((resolve) => open({ kind: 'confirm', options, resolve })),
    [open],
  );

  const alert = useCallback<AlertFn>(
    (options) =>
      new Promise<void>((resolve) => open({ kind: 'alert', options, resolve: () => resolve() })),
    [open],
  );

  const handleConfirm = useCallback(async () => {
    const request = current.current;
    const action = request?.kind === 'confirm' ? request.options.onConfirm : undefined;
    if (!action) {
      settle(true);
      return;
    }

    setPending(true);
    setError(null);
    try {
      await action();
      settle(true);
    } catch (failure) {
      // Deliberately left open: the user asked for something that did not
      // happen, and closing would be the same silence window.confirm had.
      setPending(false);
      setError(
        failure instanceof Error && failure.message
          ? failure.message
          : t('Confirm.Failed', 'The action could not be completed.'),
      );
    }
  }, [settle, t]);

  const value = useMemo(() => ({ confirm, alert }), [confirm, alert]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {request && (
        <ConfirmDialog
          title={request.options.title}
          message={request.options.message}
          // A question is destructive until told otherwise; a message is not.
          variant={request.options.variant ?? (request.kind === 'alert' ? 'info' : 'danger')}
          icon={request.options.icon}
          size={request.options.size}
          confirmLabel={
            request.kind === 'alert' ? request.options.dismissLabel : request.options.confirmLabel
          }
          cancelLabel={request.kind === 'confirm' ? request.options.cancelLabel : undefined}
          hideCancel={request.kind === 'alert'}
          pending={pending}
          error={error}
          onCancel={() => settle(false)}
          onConfirm={() => void handleConfirm()}
        />
      )}
    </ConfirmContext.Provider>
  );
}

function useDialogs() {
  const dialogs = useContext(ConfirmContext);
  if (!dialogs) throw new Error('useConfirm/useAlert require a <ConfirmProvider> above them.');
  return dialogs;
}

export function useConfirm(): ConfirmFn {
  return useDialogs().confirm;
}

export function useAlert(): AlertFn {
  return useDialogs().alert;
}
